import Section from '../components/shared/Section'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import SEO from '../components/shared/SEO'
import { ROUTE_META } from '../lib/seo'

export default function Services() {
  const areas = [
    {
      heading: "Getting the question right before you build anything.",
      body: [
        "The most expensive mistakes in any project happen at the beginning — when the wrong question gets asked, or the right question never gets asked at all. A solution gets decided before anyone has properly understood the problem. A market gets assumed rather than researched. A technology gets chosen because it's familiar, not because it fits.",
        "I work at this stage to make sure you're building the right thing before anyone writes a line of code or designs a single screen. That means genuine discovery — not theatre. It means being willing to find out that the original idea needs reshaping.",
      ],
      outcome:
        "What you get: clarity on the actual problem, a validated direction, and confidence that what you're about to build is worth building.",
    },
    {
      heading: "Designing the thing properly.",
      body: [
        "Human-centred design, service design, UX — these methodologies exist for good reasons. They work when the conditions are right: when research is genuinely exploratory, when findings can shape decisions, when the people involved are willing to be surprised by what they learn.",
        "I bring the methods and the conditions. Research that actually finds things. Journey mapping and service blueprinting that reflects reality rather than aspiration. Prototypes built to learn from, not to sell internally. Design that accounts for the full service — not just the screen.",
      ],
      outcome:
        "What you get: a designed service or product grounded in real user behaviour, with the operational reality built in from the start.",
    },
    {
      heading: "Building the operational foundation.",
      body: [
        "A great product needs operational infrastructure that actually works — processes, systems, data, tools. These things are often the last to get designed and the first to create problems at scale.",
        "I bring Six Sigma rigour, systems thinking and 30 years of operational design experience to build infrastructure that holds up under pressure. Not documented processes for their own sake — working systems that your team can use and that scale as you grow.",
      ],
      outcome:
        "What you get: operational design and infrastructure built to work from the start, not retrofitted after the fact.",
    },
    {
      heading: "A senior partner throughout.",
      body: [
        "Sometimes what's needed isn't a project — it's a person. Someone operating at COO, CPO or Head of Product level who is genuinely inside the business, making decisions, and accountable to the outcome.",
        "I work fractionally and on a retained basis with founders and leadership teams who need that kind of partner but aren't ready, or don't want, to hire full-time. I take on a small number of engagements at a time so I can give each one proper attention. When I'm in, I'm in.",
      ],
      outcome:
        "What you get: a senior operational or product partner who thinks and acts like a co-founder, on terms that make sense for where you are.",
    },
  ]

  const colors = ['neon-lime', 'neon-pink', 'neon-orange', 'neon-purple']

  return (
    <>
      <SEO
        title={ROUTE_META['/services'].title}
        description={ROUTE_META['/services'].description}
        jsonLd={ROUTE_META['/services'].jsonLd}
      />

      {/* Hero Section */}
      <Section>
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl text-neon-lime mb-6">
            How I work with you.
          </h1>
          <p className="text-xl text-neutral-300 mb-4">
            I don't offer a menu. I start with what you're trying to build.
          </p>
        </div>
      </Section>

      {/* What is service design */}
      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-orange mb-8">
            What service design actually is.
          </h2>
          <div className="space-y-6">
            <p className="text-xl text-neutral-400 leading-relaxed">
              Every product exists within a wider service and human experience. Customers do not simply use the thing you built — they discover it, evaluate it, buy it, learn how to use it, seek help when something goes wrong, and eventually decide whether it still fits their needs. Each of these moments shapes the overall relationship and experience.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Service design is the discipline of designing that wider experience — not only the visible interactions people have with a product or organisation, but also the operations, systems, policies, and processes behind the scenes that make those experiences possible. When these elements are designed together, services feel coherent, supportive, and effective. When they are not, even a well-designed product can be undermined by poor operations, fragmented systems, or experiences that fail to meet real human needs.
            </p>
            <p className="text-xl text-neutral-300">
              McKinsey's research across 300 companies found that those who lead on design grow revenue at twice the rate of their industry. The difference isn't talent or budget — it's whether design is treated as a core business discipline or an afterthought.
            </p>
          </div>
        </div>
      </Section>

      {/* What I bring — the three disciplines */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl text-center text-neon-purple mb-12">
            What I bring to the table.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <h3 className="text-2xl text-neon-lime mb-3">Analytical rigour</h3>
              <p className="text-neutral-400 leading-relaxed">
                Six Sigma Black Belt. Systems thinking. Process design. I measure and diagnose with the same discipline you'd apply to finances or engineering.
              </p>
            </Card>
            <Card>
              <h3 className="text-2xl text-neon-pink mb-3">Human-centred design</h3>
              <p className="text-neutral-400 leading-relaxed">
                UX research. Service blueprinting. Journey mapping. Co-design. I start with real users in real contexts — not assumptions or internal opinion.
              </p>
            </Card>
            <Card>
              <h3 className="text-2xl text-neon-orange mb-3">Business thinking</h3>
              <p className="text-neutral-400 leading-relaxed">
                d.MBA. Commercial strategy. Value proposition design. I connect what's right for users to what works for the business — because both have to be true.
              </p>
            </Card>
          </div>
          <p className="text-lg text-neutral-500 text-center mt-10">
            Most people specialise in one of these. I've spent 30 years integrating all three — because real-world delivery requires all of them working together.
          </p>
        </div>
      </Section>

      {/* Service Areas */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl text-neutral-100 mb-4">
              Where I typically fit.
            </h2>
            <p className="text-lg text-neutral-400">
              Most of the work I do sits at one of four points in the journey from vision to working product.
            </p>
          </div>
          <div className="space-y-12">
            {areas.map((area, index) => {
              const color = colors[index % colors.length]
              return (
                <div key={index} className={`border-l-2 border-${color} pl-8 py-2`}>
                  <h3 className={`text-3xl text-${color} mb-6`}>
                    {area.heading}
                  </h3>
                  <div className="space-y-4 mb-6">
                    {area.body.map((paragraph, idx) => (
                      <p key={idx} className="text-lg text-neutral-400 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  <p className="text-neutral-300 font-medium">
                    {area.outcome}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </Section>

      {/* How we work together */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-orange mb-8">How we start.</h2>
          <p className="text-xl text-neutral-400 leading-relaxed mb-6">
            Every engagement starts with a conversation about what you're trying to build — not a proposal template. If it's the right fit, we'll agree on a scope, a way of working and what success looks like.
          </p>
          <p className="text-xl text-neutral-400 leading-relaxed mb-6">
            I work on a project basis, a retainer basis, or fractionally depending on what makes sense. You're hiring experience and judgement — not a process or a methodology. The methods serve the work, not the other way around.
          </p>
          <p className="text-lg text-neutral-500">
            I operate through Mathos Consult Ltd, established 2003.
          </p>
        </div>
      </Section>

      {/* Sectors */}
      <Section background="gray">
        <div className="text-center max-w-3xl mx-auto">
          <h3 className="text-2xl text-neutral-300 mb-4">Sectors</h3>
          <p className="text-lg text-neutral-400">
            Fintech · Payments · Healthcare (NHS) · Government (UK GDS / Dubai KHDA) · Enterprise technology · FMCG · Education · Local government
          </p>
        </div>
      </Section>

      {/* CTA Section */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xl md:text-2xl text-neutral-100 mb-8 leading-relaxed">
            If you're looking for someone to validate a decision you've already made, I'm probably not the right fit. If you're looking for a partner who will help you ask the right questions — and then build the right thing — let's talk.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Button variant="primary" size="large" onClick={() => window.location.href = '/contact#booking'}>
              Book a discovery call
            </Button>
            <a href="mailto:hello@mathosconsult.com">
              <Button variant="secondary" size="large">Get in touch</Button>
            </a>
          </div>
        </div>
      </Section>
    </>
  )
}
